import Entity, { EntityTypes } from './entity';

export class InternalAuditScopeItem extends Entity {
  plannedOn?: Date;

  clone(): InternalAuditScopeItem {
    const item = new InternalAuditScopeItem(this.entityId, this.typeOfEntity, this.entityName, this.entityCode);
    item.plannedOn = this.plannedOn ? new Date(this.plannedOn) : undefined;

    return item;
  }
}

/**
 * A relation between two items that are both in the scope of an audit. The scope tree is built from
 * these relations, so an item can appear under more than one parent.
 */
export class InternalAuditScopeRelation {
  fromEntityType: EntityTypes;

  fromEntityId: number;

  toEntityType: EntityTypes;

  toEntityId: number;

  constructor() {
    this.fromEntityType = EntityTypes.NotSet;
    this.fromEntityId = 0;
    this.toEntityType = EntityTypes.NotSet;
    this.toEntityId = 0;
  }
}

/**
 * An item that can be added to the scope, with the score that ranks it. The score is calculated
 * server-side: half of it is how long ago the item was last audited, half is the exposure of the
 * risks connected to it.
 */
export class InternalAuditScopeCandidate {
  entity: Entity;

  isoNormIds: number[];

  normIds: number[];

  score: number;

  lastAuditedOn?: Date;

  riskScore: number;

  riskCount: number;

  constructor() {
    this.entity = new Entity();
    this.isoNormIds = [];
    this.normIds = [];
    this.score = 0;
    this.riskScore = 0;
    this.riskCount = 0;
  }
}

/**
 * An AI-suggested scope item with the reason why the current occurrence should cover it.
 */
export class InternalAuditScopeSuggestion {
  entity: Entity;

  reason?: string;

  constructor() {
    this.entity = new Entity();
  }
}

/**
 * The scope of one audit without the relations between its items: what the audits list searches on.
 * Fetched for every audit in one call, so the search can match on scope without a round trip.
 */
export class InternalAuditScopeIndex {
  internalAuditId: number;

  items: InternalAuditScopeItem[];

  constructor() {
    this.internalAuditId = 0;
    this.items = [];
  }
}

export class InternalAuditScope {
  internalAuditId: number;

  items: Entity[];

  relations: InternalAuditScopeRelation[];

  constructor() {
    this.internalAuditId = 0;
    this.items = [];
    this.relations = [];
  }

  hasItem(entity: Entity): boolean {
    return this.items.some((i) => i.typeOfEntity === entity.typeOfEntity && i.entityId === entity.entityId);
  }

  getItemsOfType(type: EntityTypes): InternalAuditScopeItem[] {
    return this.items.filter((i) => i.typeOfEntity === type);
  }

  clone(): InternalAuditScope {
    const item = new InternalAuditScope();
    item.internalAuditId = this.internalAuditId;
    item.items = this.items.map((i) => i.clone());
    item.relations = this.relations.map((r) => {
      const relation = new InternalAuditScopeRelation();
      relation.fromEntityType = r.fromEntityType;
      relation.fromEntityId = r.fromEntityId;
      relation.toEntityType = r.toEntityType;
      relation.toEntityId = r.toEntityId;

      return relation;
    });

    return item;
  }
}
